import React, { useEffect, useState } from 'react'
import * as Yup from 'yup'

import { useFormik, Form, FormikProvider } from 'formik'

import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'

import FileUpload from './FileUpload'
import NumberFormatCustom from './NumberFormatCustom'

import uploadService from '../services/upload'
import { fCurrency } from '../utils/formatNumber'
import { fDate, fDateTime } from '../utils/formatTime'

function CreditChargeDialog({ open, onClose, onClick, invoices }) {
  const [invoice, setInvoice] = useState('')
  const [investorDescription, setInvestorDescription] = useState('')
  const [chargeOrReject, setChargeOrReject] = useState(false)

  const [receiptFile, setReceiptFile] = useState({
    url: '',
    uploading: false,
  })

  const CreditChargeSchema = Yup.object().shape({
    amount: Yup.string().required('مبلغ الزامی می باشد'),
    description: Yup.string().required('توضیحات الزامی می باشد'),
  })

  const formik = useFormik({
    initialValues: {
      amount: '',
      description: '',
      file: '',
      status: '',
    },
    validationSchema: CreditChargeSchema,
    onSubmit: (values, actions) => {
      setTimeout(() => {
        if (receiptFile.url) {
          values.file = receiptFile.url
        }

        values.status = 'ACCEPTED'
        values.invoiceId = invoice
        onClick(values)
        actions.setSubmitting(false)
        resetForm()
      }, 500)
    },
  })

  const handleSelectInvoice = async (e) => {
    setInvoice(e.target.value)
    setChargeOrReject(true)
    const invoice = invoices.find((invoice) => invoice._id === e.target.value)

    formik.setFieldValue('amount', invoice?.amount)
    setInvestorDescription(invoice?.description)
    setReceiptFile({ url: invoice?.receiptFile })
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const onReceiptFileDrop = async (acceptedFiles) => {
    try {
      setReceiptFile({ uploading: true })

      const file = acceptedFiles[0]
      const url = await uploadService(file)

      setReceiptFile({ url, uploading: false })
    } catch (error) {
      console.log(error)
    }
  }

  const onReceiptFileDelete = () => {
    setReceiptFile({ url: '' })
  }

  const handleReject = () => {
    const values = {
      invoiceId: invoice,
      status: 'REJECTED',
    }
    onClick(values)
  }

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
  } = formik

  return (
    <Dialog open={open} onClose={handleClose} fullWidth='xs' maxWidth='xs'>
      <DialogTitle id='alert-dialog-title'>شارژ اعتبار</DialogTitle>
      <DialogContent>
        <FormikProvider value={formik}>
          <Form
            id='credit-form'
            autoComplete='off'
            noValidate
            onSubmit={handleSubmit}
          >
            <Box marginTop='5px'>
              <FormControl fullWidth>
                <InputLabel id='demo-multiple-checkbox-label'>
                  فاکتور
                </InputLabel>
                <Select
                  labelId='demo-simple-select-label'
                  id='demo-simple-select'
                  label='فاکتور'
                  value={invoice}
                  onChange={(e) => handleSelectInvoice(e)}
                >
                  {invoices.map((invoice) => (
                    <MenuItem value={invoice._id}>
                      مبلغ: {fCurrency(invoice?.amount)} - تاریخ:{' '}
                      {fDate(invoice?.createdAt)} -{' '}
                      {fDateTime(invoice?.createdAt)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {invoice && (
                <>
                  <TextField
                    name='amountValue'
                    id='outlined-basic'
                    fullWidth
                    label='مبلـغ'
                    {...getFieldProps('amount')}
                    variant='outlined'
                    sx={{
                      display: 'block',
                      margin: '20px 0',
                    }}
                    error={Boolean(touched.amount && errors.amount)}
                    helperText={touched.amount && errors.amount}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='start'>ریال</InputAdornment>
                      ),
                      inputComponent: NumberFormatCustom,
                    }}
                    inputProps={{
                      readOnly: true,
                    }}
                    autoComplete='off'
                  />
                  <TextField
                    id='investor-description'
                    label='توضیحات سرمایه گذار'
                    value={investorDescription}
                    fullWidth
                    multiline
                    rows={5}
                    autoComplete='off'
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    sx={{ width: 1, mt: 2 }}
                    id='outlined-multiline-flexible'
                    label='توضیحات'
                    {...getFieldProps('description')}
                    fullWidth
                    multiline
                    rows={5}
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                    autoComplete='off'
                  />
                  <Box sx={{ width: 1, mt: 2 }}>
                    <FileUpload
                      uploading={receiptFile.uploading}
                      onDrop={onReceiptFileDrop}
                      file={receiptFile.url}
                      buttonText='آپلود فایل'
                      onDelete={onReceiptFileDelete}
                      isDialog
                      showFile
                    />
                  </Box>
                </>
              )}
            </Box>
          </Form>
        </FormikProvider>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-around',
          'button:nth-child(3)': {
            marginLeft: 'auto',
          },
        }}
      >
        <LoadingButton
          form='credit-form'
          type='submit'
          variant='contained'
          loading={isSubmitting}
          disabled={!chargeOrReject}
        >
          ثبت
        </LoadingButton>
        <Button
          onClick={handleClose}
          sx={{
            color: '#fff',
            backgroundColor: '#E75C25',
            '&:hover': {
              backgroundColor: '#E75C25',
            },
          }}
        >
          بستن
        </Button>
        <Button
          onClick={handleReject}
          disabled={!chargeOrReject}
          sx={{
            color: '#fff',
            backgroundColor: 'red',
            '&:hover': {
              backgroundColor: 'red',
            },
          }}
        >
          رد فاکتور
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreditChargeDialog
